import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Facebook, Twitter, Linkedin, Instagram, Mail, MapPin, Phone, Send, CheckCircle } from 'lucide-react';
import { settingsAPI, contactAPI } from '../services/api';

const Footer: React.FC = () => {
  const location = useLocation();
  const [settings, setSettings] = useState<any>({
    contact_email: 'bestscholarsafrica@gmail.com',
    contact_email_alt: 'bsafofficial@outlook.com',
    contact_phone: '+250 788 932 662',
    contact_address: 'Kigali, Rwanda'
  });
  const [email, setEmail] = useState('');
  const [subStatus, setSubStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [subMsg, setSubMsg] = useState('');

  useEffect(() => { fetchSettings(); }, []);

  const fetchSettings = async () => {
    try {
      const response = await settingsAPI.getAll({ category: 'contact' });
      const data = response?.data?.data;
      if (typeof data === 'object' && data !== null) {
        setSettings((prev: any) => ({ ...prev, ...data }));
      }
    } catch {}
  };

  const handleSubscribe = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email) return;
    setSubStatus('loading');
    try {
      await contactAPI.subscribe({ email });
      setSubStatus('success');
      setSubMsg('You\'re subscribed! Welcome to BSAF.');
      setEmail('');
    } catch (err: any) {
      const msg = err?.response?.data?.message || 'Something went wrong. Try again.';
      setSubStatus('error');
      setSubMsg(msg);
    }
  };

  if (location.pathname === '/dashboard' || location.pathname.startsWith('/admin')) {
    return null;
  }

  return (
    <footer className="bg-gray-900 text-white mt-auto">

      {/* Newsletter Banner */}
      <div className="bg-gradient-to-r from-blue-700 to-indigo-700">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="text-center md:text-left">
              <h3 className="text-xl font-bold text-white mb-1">Stay in the loop</h3>
              <p className="text-blue-100 text-sm">Get the latest jobs, scholarships & opportunities delivered to your inbox.</p>
            </div>

            {subStatus === 'success' ? (
              <div className="flex items-center gap-2 bg-white/10 text-white px-5 py-3 rounded-xl text-sm font-medium">
                <CheckCircle className="w-5 h-5 text-green-300 flex-shrink-0" />
                {subMsg}
              </div>
            ) : (
              <form onSubmit={handleSubscribe} className="flex flex-col sm:flex-row gap-3 w-full md:w-auto">
                <input
                  type="email"
                  value={email}
                  onChange={(e) => { setEmail(e.target.value); setSubStatus('idle'); }}
                  placeholder="Enter your email address"
                  required
                  className="flex-1 min-w-0 sm:w-72 px-4 py-3 rounded-xl text-gray-900 text-sm focus:outline-none focus:ring-2 focus:ring-white"
                />
                <button
                  type="submit"
                  disabled={subStatus === 'loading'}
                  className="flex items-center justify-center gap-2 px-6 py-3 bg-white text-blue-700 font-semibold rounded-xl hover:bg-blue-50 transition disabled:opacity-60 whitespace-nowrap text-sm"
                >
                  {subStatus === 'loading' ? (
                    <div className="w-4 h-4 border-2 border-blue-700 border-t-transparent rounded-full animate-spin" />
                  ) : (
                    <Send className="w-4 h-4" />
                  )}
                  Subscribe
                </button>
              </form>
            )}
          </div>
          {subStatus === 'error' && (
            <p className="text-red-300 text-xs mt-3 text-center md:text-right">{subMsg}</p>
          )}
        </div>
      </div>

      {/* Main Footer */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-8 mb-8">
          <div>
            <Link to="/" className="flex items-center gap-2 mb-4">
              <div className="bg-white rounded-xl p-2 inline-flex">
                <img src="/images/logo.png" alt="BSAF" style={{ height: '120px' }} className="w-auto" />
              </div>
            </Link>
            <p className="text-gray-400 text-sm leading-relaxed mb-4">
              Building Africa's Smart Generation through verified opportunities and digital services.
            </p>
            <div className="flex gap-3">
              <a href="https://facebook.com" target="_blank" rel="noopener noreferrer" className="w-9 h-9 bg-gray-800 rounded-lg flex items-center justify-center hover:bg-blue-600 transition">
                <Facebook className="w-4 h-4" />
              </a>
              <a href="https://twitter.com" target="_blank" rel="noopener noreferrer" className="w-9 h-9 bg-gray-800 rounded-lg flex items-center justify-center hover:bg-blue-400 transition">
                <Twitter className="w-4 h-4" />
              </a>
              <a href="https://linkedin.com" target="_blank" rel="noopener noreferrer" className="w-9 h-9 bg-gray-800 rounded-lg flex items-center justify-center hover:bg-blue-700 transition">
                <Linkedin className="w-4 h-4" />
              </a>
              <a href="https://instagram.com" target="_blank" rel="noopener noreferrer" className="w-9 h-9 bg-gray-800 rounded-lg flex items-center justify-center hover:bg-pink-600 transition">
                <Instagram className="w-4 h-4" />
              </a>
            </div>
          </div>

          <div>
            <h3 className="font-semibold text-lg mb-4">Quick Links</h3>
            <ul className="space-y-2 text-sm">
              <li><Link to="/jobs" className="text-gray-400 hover:text-white transition">Browse Jobs</Link></li>
              <li><Link to="/opportunities" className="text-gray-400 hover:text-white transition">Opportunities</Link></li>
              <li><Link to="/blogs" className="text-gray-400 hover:text-white transition">Blog & Articles</Link></li>
              <li><Link to="/services" className="text-gray-400 hover:text-white transition">Our Services</Link></li>
              <li><Link to="/about" className="text-gray-400 hover:text-white transition">About Us</Link></li>
            </ul>
          </div>

          <div>
            <h3 className="font-semibold text-lg mb-4">For Businesses</h3>
            <ul className="space-y-2 text-sm">
              <li><Link to="/post-job" className="text-gray-400 hover:text-white transition">Post a Job</Link></li>
              <li><Link to="/pricing" className="text-gray-400 hover:text-white transition">Pricing Plans</Link></li>
              <li><Link to="/faq" className="text-gray-400 hover:text-white transition">FAQ</Link></li>
            </ul>
          </div>

          <div>
            <h3 className="font-semibold text-lg mb-4">Contact Us</h3>
            <ul className="space-y-3 text-sm">
              <li className="flex items-start gap-2 text-gray-400">
                <Mail className="w-4 h-4 mt-0.5 flex-shrink-0" />
                <div className="flex flex-col">
                  <a href={`mailto:${settings.contact_email}`} className="hover:text-white transition">{settings.contact_email}</a>
                  <a href={`mailto:${settings.contact_email_alt || 'bsafofficial@outlook.com'}`} className="hover:text-white transition text-xs mt-0.5">{settings.contact_email_alt || 'bsafofficial@outlook.com'}</a>
                </div>
              </li>
              <li className="flex items-center gap-2 text-gray-400">
                <Phone className="w-4 h-4 flex-shrink-0" />
                <span>{settings.contact_phone}</span>
              </li>
              <li className="flex items-start gap-2 text-gray-400">
                <MapPin className="w-4 h-4 mt-0.5 flex-shrink-0" />
                <span>{settings.contact_address}</span>
              </li>
            </ul>
          </div>
        </div>

        <div className="border-t border-gray-800 pt-8 flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="text-sm text-gray-400">
            © {new Date().getFullYear()} BSAF. All rights reserved.
          </p>
          <div className="flex gap-6 text-sm text-gray-400">
            <Link to="/privacy" className="hover:text-white transition">Privacy Policy</Link>
            <Link to="/terms" className="hover:text-white transition">Terms of Service</Link>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
